/**
 * Simple request cache to prevent duplicate API calls
 * Survives component remounts
 */

interface CacheEntry<T> {
  promise: Promise<T>;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<any>>();
const CACHE_TTL = 5000; // 5 seconds

/**
 * Deduplicate requests - if the same request is in flight, return the existing promise
 */
export function dedupeRequest<T>(
  key: string,
  requestFn: () => Promise<T>
): Promise<T> {
  const now = Date.now();
  const existing = cache.get(key);

  // If we have a recent request in flight, return it
  if (existing && now - existing.timestamp < CACHE_TTL) {
    return existing.promise;
  }

  // Start new request
  const promise = requestFn().finally(() => {
    // Clean up after request completes
    setTimeout(() => cache.delete(key), CACHE_TTL);
  });

  cache.set(key, { promise, timestamp: now });
  return promise;
}

/**
 * Clear all cached requests
 */
export function clearRequestCache() {
  cache.clear();
}

/**
 * Clear a specific cached request
 */
export function clearCacheKey(key: string) {
  cache.delete(key);
}
